"use client";

import { useMemo, useState } from "react";
import {
  Checkbox,
  CopyButton,
  DataTable,
  Notice,
  PillGroup,
  SelectField,
  StatCard,
  StatGrid,
  TextAreaField,
} from "@/components/tool-kit/primitives";
import {
  HINT,
  RESULT_CARD,
  WIDGET_H2,
  WIDGET_H3,
} from "@/components/tool-kit/styles";
import {
  useToolLocale,
  useToolNumber,
  useToolUi,
} from "@/components/tools/tool-ui";
import { t, tPlural } from "@/lib/i18n/interpolate";
import {
  type CheckResult,
  type Compare,
  checkPalindrome,
  type PalindromeOptions,
  type ScanResult,
  scanForPalindromes,
} from "@/lib/tool-kit/word-games/palindrome";

/** English strings. A translated content file overrides these through `ui`. */
export const PALINDROME_CHECKER_STRINGS = {
  modeLabel: "What do you want to do?",
  modes: { check: "Check a phrase", find: "Find palindromes in a text" },
  checkLabel: "Word, number or phrase",
  checkHint:
    "One per line to check several at once. Spaces, punctuation and capitals are ignored unless you change the options.",
  checkPlaceholder: "A man, a plan, a canal: Panama",
  findLabel: "Text to search",
  findHint:
    "Paste a paragraph. The search lists palindromic words and sentences and the longest palindromic stretch.",
  findPlaceholder:
    "Anna and Bob saw a kayak at noon. Was it a car or a cat I saw?",
  tryLabel: "Try:",
  examples: [
    "A man, a plan, a canal: Panama",
    "Was it a car or a cat I saw?",
    "Never odd or even",
    "12321",
  ],
  ignoreCase: "Ignore capital letters",
  ignorePunctuation: "Ignore spaces and punctuation",
  ignoreAccents: "Ignore accents (é counts as e)",
  compareLabel: "Compare by",
  compares: { letters: "Letters", words: "Whole words" },
  minLabel: "Shortest palindrome to list",
  minOption: "one:{count} letter|other:{count} letters",
  emptyPrompt: "Type or paste something to check.",
  emptyFind: "Paste a text to search for palindromes.",
  heading: "Result",
  yes: "Palindrome",
  no: "Not a palindrome",
  nothing: "Nothing to compare",
  yesDetail: "It reads the same forwards and backwards.",
  statLetters: "Letters compared",
  statWords: "Words compared",
  comparedLabel: "As compared",
  reversedLabel: "Backwards",
  mismatch:
    "Position {index} from the start ({left}) does not match position {index} from the end ({right}).",
  linesHeading: "Line by line",
  columns: { line: "#", text: "Text", result: "Result" },
  findHeading: "What the text contains",
  statWordsFound: "Palindromic words",
  statSentences: "Palindromic sentences",
  statLongest: "Longest stretch",
  wordsHeading: "Palindromic words",
  noWords: "No palindromic words of this length.",
  sentencesHeading: "Palindromic sentences",
  noSentences: "No palindromic sentences.",
  longestHeading: "Longest palindromic stretch",
  longestLength: "one:{count} letter|other:{count} letters",
  noLongest: "No palindrome of two or more letters.",
  copyResult: "Copy result",
  copied: "Copied!",
  privacyNote: "Runs entirely in your browser — nothing is uploaded.",
} as const;

type Strings = typeof PALINDROME_CHECKER_STRINGS;
type Mode = "check" | "find";

const MIN_LENGTHS = [2, 3, 4, 5, 6];
const RE_LINE_BREAK = /\r?\n/;
const PREVIEW = 60;

function preview(text: string): string {
  return text.length > PREVIEW ? `${text.slice(0, PREVIEW)}…` : text;
}

function Verdict({
  check,
  compare,
  strings,
}: {
  check: CheckResult;
  compare: Compare;
  strings: Strings;
}) {
  const fmt = useToolNumber();
  const joiner = compare === "words" ? " " : "";
  const forwards = check.units.join(joiner);
  const backwards = [...check.units].reverse().join(joiner);
  let verdict: string = strings.nothing;
  if (check.units.length > 0) {
    verdict = check.palindrome ? strings.yes : strings.no;
  }
  return (
    <div className="space-y-4">
      <div className={RESULT_CARD}>
        <p className="font-semibold text-2xl text-foreground">{verdict}</p>
        {check.palindrome ? (
          <p className="mt-1 text-muted-foreground text-sm">
            {strings.yesDetail}
          </p>
        ) : null}
        {check.mismatch ? (
          <p className="mt-1 text-muted-foreground text-sm">
            {t(strings.mismatch, {
              index: fmt(check.mismatch.index + 1),
              left: check.mismatch.left,
              right: check.mismatch.right,
            })}
          </p>
        ) : null}
      </div>
      <StatGrid>
        <StatCard
          label={compare === "words" ? strings.statWords : strings.statLetters}
          value={fmt(check.units.length)}
        />
      </StatGrid>
      {check.units.length > 0 ? (
        <dl className="space-y-2 text-sm">
          <div>
            <dt className={HINT}>{strings.comparedLabel}</dt>
            <dd className="break-words font-mono text-foreground">
              {forwards}
            </dd>
          </div>
          <div>
            <dt className={HINT}>{strings.reversedLabel}</dt>
            <dd className="break-words font-mono text-foreground">
              {backwards}
            </dd>
          </div>
        </dl>
      ) : null}
    </div>
  );
}

function Lines({
  lines,
  compare,
  options,
  strings,
}: {
  lines: readonly string[];
  compare: Compare;
  options: PalindromeOptions;
  strings: Strings;
}) {
  const fmt = useToolNumber();
  const rows = lines.map((line, index) => {
    const check = checkPalindrome(line, compare, options);
    let verdict: string = strings.nothing;
    if (check.units.length > 0) {
      verdict = check.palindrome ? strings.yes : strings.no;
    }
    return [fmt(index + 1), preview(line), verdict];
  });
  return (
    <div className="space-y-3">
      <h3 className={WIDGET_H3}>{strings.linesHeading}</h3>
      <DataTable
        head={[
          strings.columns.line,
          strings.columns.text,
          strings.columns.result,
        ]}
        rows={rows}
      />
    </div>
  );
}

function Scan({ scan, strings }: { scan: ScanResult; strings: Strings }) {
  const locale = useToolLocale();
  const fmt = useToolNumber();
  return (
    <div aria-live="polite" className="space-y-5">
      <h2 className={WIDGET_H2}>{strings.findHeading}</h2>
      <StatGrid>
        <StatCard
          emphasis
          label={strings.statWordsFound}
          value={fmt(scan.words.length)}
        />
        <StatCard
          label={strings.statSentences}
          value={fmt(scan.sentences.length)}
        />
        <StatCard
          label={strings.statLongest}
          value={fmt(scan.longest?.length ?? 0)}
        />
      </StatGrid>
      <section className="space-y-2">
        <h3 className={WIDGET_H3}>{strings.wordsHeading}</h3>
        {scan.words.length === 0 ? (
          <p className="text-muted-foreground text-sm">{strings.noWords}</p>
        ) : (
          <ul className="flex flex-wrap gap-2">
            {scan.words.map((entry) => (
              <li
                className="rounded-md border border-border bg-card px-2.5 py-1 font-mono text-foreground text-sm"
                key={entry.word}
              >
                {entry.word}
                {entry.count > 1 ? (
                  <span className={HINT}> ×{fmt(entry.count)}</span>
                ) : null}
              </li>
            ))}
          </ul>
        )}
      </section>
      <section className="space-y-2">
        <h3 className={WIDGET_H3}>{strings.sentencesHeading}</h3>
        {scan.sentences.length === 0 ? (
          <p className="text-muted-foreground text-sm">{strings.noSentences}</p>
        ) : (
          <ul className="space-y-1.5">
            {scan.sentences.map((sentence) => (
              <li className="text-foreground text-sm" key={sentence}>
                {sentence}
              </li>
            ))}
          </ul>
        )}
      </section>
      <section className="space-y-2">
        <h3 className={WIDGET_H3}>{strings.longestHeading}</h3>
        {scan.longest ? (
          <div className={RESULT_CARD}>
            <p className="break-words text-foreground">{scan.longest.text}</p>
            <p className={`${HINT} mt-1`}>
              {tPlural(strings.longestLength, locale, scan.longest.length, {
                count: fmt(scan.longest.length),
              })}
            </p>
          </div>
        ) : (
          <p className="text-muted-foreground text-sm">{strings.noLongest}</p>
        )}
      </section>
    </div>
  );
}

export default function PalindromeCheckerWidget() {
  const strings = useToolUi(PALINDROME_CHECKER_STRINGS);
  const locale = useToolLocale();
  const fmt = useToolNumber();
  const [mode, setMode] = useState<Mode>("check");
  const [text, setText] = useState("");
  const [ignoreCase, setIgnoreCase] = useState(true);
  const [ignorePunctuation, setIgnorePunctuation] = useState(true);
  const [ignoreAccents, setIgnoreAccents] = useState(true);
  const [compare, setCompare] = useState<Compare>("letters");
  const [minLength, setMinLength] = useState(3);
  const options: PalindromeOptions = {
    ignoreCase,
    ignorePunctuation,
    ignoreAccents,
  };
  const lines = useMemo(
    () =>
      text
        .split(RE_LINE_BREAK)
        .map((line) => line.trim())
        .filter((line) => line.length > 0),
    [text]
  );
  const single = useMemo(
    () =>
      lines.length === 1
        ? checkPalindrome(lines[0], compare, {
            ignoreCase,
            ignorePunctuation,
            ignoreAccents,
          })
        : null,
    [lines, compare, ignoreCase, ignorePunctuation, ignoreAccents]
  );
  const scan = useMemo(
    () =>
      mode === "find" && text.trim()
        ? scanForPalindromes(
            text,
            { ignoreCase, ignorePunctuation: true, ignoreAccents },
            minLength
          )
        : null,
    [mode, text, ignoreCase, ignoreAccents, minLength]
  );

  function summary(): string {
    if (!single) {
      return "";
    }
    if (single.units.length === 0) {
      return strings.nothing;
    }
    return `${lines[0]} — ${single.palindrome ? strings.yes : strings.no}`;
  }

  return (
    <>
      <PillGroup
        label={strings.modeLabel}
        name="palindrome-mode"
        onChange={setMode}
        options={[
          { value: "check", label: strings.modes.check },
          { value: "find", label: strings.modes.find },
        ]}
        value={mode}
      />
      <TextAreaField
        hint={mode === "check" ? strings.checkHint : strings.findHint}
        id="palindrome-text"
        label={mode === "check" ? strings.checkLabel : strings.findLabel}
        onChange={(value) => setText(value.slice(0, 50_000))}
        placeholder={
          mode === "check" ? strings.checkPlaceholder : strings.findPlaceholder
        }
        prose
        rows={mode === "check" ? 3 : 8}
        value={text}
      />
      {mode === "check" ? (
        <p className="flex flex-wrap items-center gap-2 text-sm">
          <span className={HINT}>{strings.tryLabel}</span>
          {strings.examples.map((example) => (
            <button
              className="font-medium text-primary underline-offset-4 hover:underline"
              key={example}
              onClick={() => setText(example)}
              type="button"
            >
              {example}
            </button>
          ))}
        </p>
      ) : null}
      <div className="flex flex-wrap items-center gap-x-6 gap-y-3">
        <Checkbox
          checked={ignoreCase}
          label={strings.ignoreCase}
          onChange={setIgnoreCase}
        />
        {mode === "check" ? (
          <Checkbox
            checked={ignorePunctuation}
            label={strings.ignorePunctuation}
            onChange={setIgnorePunctuation}
          />
        ) : null}
        <Checkbox
          checked={ignoreAccents}
          label={strings.ignoreAccents}
          onChange={setIgnoreAccents}
        />
      </div>
      {mode === "check" ? (
        <div className="max-w-xs">
          <SelectField
            id="palindrome-compare"
            label={strings.compareLabel}
            onChange={setCompare}
            options={[
              { value: "letters", label: strings.compares.letters },
              { value: "words", label: strings.compares.words },
            ]}
            value={compare}
          />
        </div>
      ) : (
        <div className="max-w-xs">
          <SelectField
            id="palindrome-min"
            label={strings.minLabel}
            onChange={(value) => setMinLength(Number(value))}
            options={MIN_LENGTHS.map((value) => ({
              value: String(value),
              label: tPlural(strings.minOption, locale, value, {
                count: fmt(value),
              }),
            }))}
            value={String(minLength)}
          />
        </div>
      )}
      {mode === "check" && lines.length === 0 ? (
        <Notice>{strings.emptyPrompt}</Notice>
      ) : null}
      {mode === "find" && !scan ? <Notice>{strings.emptyFind}</Notice> : null}
      {mode === "check" && single ? (
        <div aria-live="polite" className="space-y-4">
          <div className="flex flex-wrap items-end justify-between gap-3">
            <h2 className={WIDGET_H2}>{strings.heading}</h2>
            <CopyButton
              copiedLabel={strings.copied}
              label={strings.copyResult}
              small
              text={summary()}
            />
          </div>
          <Verdict check={single} compare={compare} strings={strings} />
        </div>
      ) : null}
      {mode === "check" && lines.length > 1 ? (
        <div aria-live="polite" className="space-y-4">
          <h2 className={WIDGET_H2}>{strings.heading}</h2>
          <Lines
            compare={compare}
            lines={lines}
            options={options}
            strings={strings}
          />
        </div>
      ) : null}
      {mode === "find" && scan ? <Scan scan={scan} strings={strings} /> : null}
      <p className={HINT}>{strings.privacyNote}</p>
    </>
  );
}
